import { useEffect, useRef, useState } from "react";

export type LandingPlace = "olive" | "coffee" | "books" | "flowers";
const places = [
  { profile: "olive", title: "Olive Studio", kind: "Ceramics · West Village", image: "studio-cover.png", latitude: 40.7358, longitude: -74.0036 },
  { profile: "coffee", title: "Hudson Coffee", kind: "Coffee · Tribeca", image: "manhattan-coffee.png", latitude: 40.7195, longitude: -74.0080 },
  { profile: "books", title: "Canal Books", kind: "Books · SoHo", image: "manhattan-books.png", latitude: 40.7248, longitude: -73.9955 },
  { profile: "flowers", title: "Chelsea Flowers", kind: "Flowers · Chelsea", image: "manhattan-flowers.png", latitude: 40.7476, longitude: -74.0030 },
] as const;

interface AppleMarker { selected: boolean }
interface AppleMap {
  addAnnotation(annotation: AppleMarker): void;
  destroy(): void;
}
interface MapKitRuntime {
  Coordinate: new (latitude: number, longitude: number) => object;
  Map: new (element: HTMLElement, options: { center: object; cameraDistance: number }) => AppleMap;
  Annotation: new (coordinate: object, factory: () => HTMLElement, options: {
    title: string;
    subtitle: string;
    accessibilityLabel: string;
    size: { width: number; height: number };
    calloutEnabled: boolean;
  }) => AppleMarker;
}
type MapWindow = Window & { mapkit?: MapKitRuntime; otcLandingMapReady?: () => void };
let mapKitLoading: Promise<MapKitRuntime> | undefined;

function loadAppleMaps() {
  if (mapKitLoading) return mapKitLoading;
  mapKitLoading = (async () => {
    const response = await fetch("/landing-demo/mapkit-config.json");
    if (!response.ok) throw new Error("Map configuration unavailable");
    const config: { token?: string } = await response.json();
    if (!config.token) throw new Error("Map configuration unavailable");
    return new Promise<MapKitRuntime>((resolve, reject) => {
      const mapWindow = window as MapWindow;
      const script = document.createElement("script");
      const timeout = window.setTimeout(() => reject(new Error("Apple Maps unavailable")), 20000);
      mapWindow.otcLandingMapReady = () => {
        window.clearTimeout(timeout);
        if (mapWindow.mapkit) resolve(mapWindow.mapkit);
        else reject(new Error("Apple Maps unavailable"));
      };
      script.src = "https://cdn.apple-mapkit.com/mk/6/mapkit.core.js";
      script.async = true;
      script.dataset.callback = "otcLandingMapReady";
      script.dataset.libraries = "map,services,annotations";
      script.dataset.token = config.token;
      script.onerror = () => {
        window.clearTimeout(timeout);
        reject(new Error("Apple Maps unavailable"));
      };
      document.head.appendChild(script);
    });
  })();
  return mapKitLoading;
}

export function LandingAppleMap({ onOpenMaker }: { onOpenMaker?: (opener?: HTMLElement, profile?: LandingPlace) => void }) {
  const container = useRef<HTMLDivElement>(null);
  const openMaker = useRef(onOpenMaker);
  useEffect(() => { openMaker.current = onOpenMaker; }, [onOpenMaker]);
  const [state, setState] = useState<"loading" | "ready" | "unavailable">("loading");
  useEffect(() => {
    let disposed = false;
    let map: AppleMap | undefined;
    const listeners: Array<{ button: HTMLButtonElement; click: (event: MouseEvent) => void }> = [];
    loadAppleMaps().then((mapkit) => {
      if (disposed || !container.current) return;
      map = new mapkit.Map(container.current, {
        center: new mapkit.Coordinate(40.7335, -74.0015),
        cameraDistance: 6500,
      });
      // Fictional examples at illustrative Manhattan coordinates, never live listings.
      for (const place of places) {
        const marker = new mapkit.Annotation(new mapkit.Coordinate(place.latitude, place.longitude), () => {
          const button = document.createElement("button");
          button.type = "button";
          button.className = "landing-maker-marker";
          button.setAttribute("aria-label", `${place.title} — illustrative maker`);
          const image = document.createElement("img");
          image.className = "landing-maker-image";
          image.src = `/landing-demo/${place.image}`;
          image.alt = "";
          const title = document.createElement("span");
          title.className = "landing-maker-title";
          title.textContent = place.title;
          const kind = document.createElement("span");
          kind.className = "landing-maker-kind";
          kind.textContent = place.kind;
          button.append(image, title, kind);
          const click = (event: MouseEvent) => {
            event.stopPropagation();
            if (disposed) return;
            marker.selected = false;
            openMaker.current?.(button, place.profile);
          };
          button.addEventListener("click", click);
          listeners.push({ button, click });
          return button;
        }, {
          title: place.title, subtitle: "Illustrative maker",
          accessibilityLabel: `${place.title} — illustrative maker`,
          size: { width: 144, height: 104 }, calloutEnabled: false,
        });
        map.addAnnotation(marker);
      }
      setState("ready");
    }).catch(() => { if (!disposed) setState("unavailable"); });
    return () => {
      disposed = true;
      for (const { button, click } of listeners) button.removeEventListener("click", click);
      map?.destroy();
    };
  }, []);
  return (
    <div className="landing-apple-map" aria-label="Apple Maps · Manhattan, New York">
      <div className="landing-apple-map-canvas" ref={container} />
      {state !== "ready" && <p className="landing-map-status" role="status">{state === "loading" ? "Loading Apple Maps…" : <a href="https://maps.apple.com/" target="_blank" rel="noreferrer">Open Apple Maps ↗</a>}</p>}
      {state === "unavailable" && onOpenMaker && <div className="landing-maker-fallback">{places.map((place) => <button key={place.profile} type="button" onClick={(event) => openMaker.current?.(event.currentTarget, place.profile)}>Open {place.title} example</button>)}</div>}
    </div>
  );
}
